import { Badge, Button, Card, Dropdown, message, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import axios from "axios";
import { BASE_API_CONFIG } from "../../util/Api";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../util/helper";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import { CopyOutlined, DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";

const ViewPolicyDetails = () => {
  const { Meta } = Card;
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const [policyList, setPolicyList] = useState({ rows: [], count: 0 });
  const [pageSize, setPageSize] = useState(10);
  const authUser = useSelector(({ auth }) => auth.userData);
  const token = authUser.token;
  const dispatch = useDispatch();

  const fetchPolicyUpdates = useCallback(
    async (page = 1, limit = pageSize) => {
      dispatch(fetchStart());
      try {
        const response = await axios.get(
          `${BASE_API_CONFIG.baseURL}/policyUpdate`,
          {
            params: { page, limit },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setPolicyList({
          rows: response.data.rows,
          count: response.data.count,
        });
        dispatch(fetchSuccess());
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchPolicyUpdates();
  }, [fetchPolicyUpdates]);

  const navigateToEditDetails = (id) => {
    history.push(`policy-updates/editDetails/${id}`);
  };
  let description =
    "Add new policy guidelines and ensure compliance with the latest trade regulations. Use this feature to keep exporters, importers, and stakeholders informed about policy changes, documentation requirements, and procedural updates.";

  const handleDelete = useCallback( async (deleteItem) => {
    try {
      const response = await axios.delete(
        `${BASE_API_CONFIG.baseURL}/policyUpdate/${deleteItem}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      message.success(response.data.message || 'Policy deactivated successfully');
      fetchPolicyUpdates();
    } catch (error) {
       message.error('Failed to deactivate policy');
      console.error("Error fetching policies:", error);
    }
  }, []);

  const navigateToViewDetails = (id) => {
    history.push(`policy-updates/views/${id}`)
  }

  const navigateToDetails = (id) => {
    history.push(`policy-updates/details`)
  }

  const moreActionsRenderer = useCallback(
    (record) => (
      <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          menu={{
            items: [
              {
                label: "View",
                key: "1",
                icon: <EditOutlined />,
                onClick: () => {
                  navigateToViewDetails(record?.id);
                },
              },
              {
                label: "Edit",
                key: "2",
                icon: <CopyOutlined />,
                onClick: () => {
                  navigateToEditDetails(record?.id, true);
                },
              },
              {
                label: "Delete",
                key: "3",
                icon: <DeleteOutlined />,
                onClick: () => {
                  handleDelete(record?.id);
                },
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined />
        </Dropdown>
      </div>
    ),
    [navigateToDetails]
  );

  const columns = useMemo(
    () => [
      {
        title: "Posted Date",
        key: "createdAt", 
        render: (record) => <div>{formatDate(record?.createdAt)}</div>,
      },
      {
        title: "Policy Title",
        key: "title",
        render: (record) => <div>{record?.title}</div>,
      },
      {
        title: "Details",
        key: "details",
        render: (record) => <div>{record?.details}</div>,
      },
      {
        title: "Actions",
        key: "actions",
        render: moreActionsRenderer,
      },
    ],
    [moreActionsRenderer]
  );

  const renderContent = useCallback(
    (data, count) => {
      return isMobile ? (
        <Card className="mobile-view">
          {data.map((record) => (
            <ItemDetailMobile
              postedDate={formatDate(record.createdAt)}
              primaryRow={{
                text: record.title,
                subText: `Effective: ${formatDate(record.withEffectiveDate)}`,
              }}
              secondaryRow={{
                text: record.typeOfUpdate,
                subText: record.isActive ? "Active" : "Inactive",
              }}
              status={record.isActive ? "success" : "default"}
              handleNavigation={() => navigateToViewDetails(record.id)}
            />
          ))}
        </Card>
      ) : (
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          className="gx-table-responsive policy-table myexim-table"
          columns={columns}
          dataSource={data}
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: true,
            pageSize: pageSize,
            total: count,
            onChange: (page, pageSize) => {
              setPageSize(pageSize);
              fetchPolicyUpdates(page, pageSize);
            },
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onRow={(record) => ({
            onClick: () => navigateToViewDetails(record.id),
          })}
        />
      );
    },
    [columns, isMobile, pageSize, fetchPolicyUpdates]
  );

  return (
    <>
      <Card
        style={{ width: "100%" }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="myexim-table-title">{"Policy Updates"}</div>

            {isArrayNonEmpty(policyList?.rows) && (
              <Button type="primary" onClick={() => navigateToDetails()}>
                Add New Policy
              </Button>
            )}
          </div>
        }
      >
        <Meta
          description={
            <div style={{ lineHeight: 1.5 }}>
              <span style={{ color: "#1677ff" }}> {description}</span>
            </div>
          }
        />
      </Card>
      {policyList.rows.length > 0 ? (
        renderContent(policyList.rows, policyList.count)
      ) : (
        <ListEmptyComponent
          message="No policy created yet, Try it now"
          buttonText="Add New Policy"
          onButtonClick={() => navigateToDetails()}
        />
      )}
    </>
  );
};

export default ViewPolicyDetails;